import { Container, DataGrid, KeenIcon } from '@/components';
import { useLayout } from '@/providers';
import {
  useCreateDMOStaffMutation,
  useDeleteDMOStaffMutation,
  useGetDMOStaffsQuery,
  useUpdateDMOStaffMutation,
} from '@/services/DMOStaffManagementService';
import { ErrorApiResponse, PaginatedApiResponse } from '@/types/api.types';
import {
  CreateDMOStaffPayload,
  DMOStaff,
  DMOStaffListQueryParams,
} from '@/types/dmo-staff-management.types';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import usePagination from '@/hooks/usePagination';
import { ColumnDef } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import DMOStaffFormModal from './DMOStaffFormModal';
import { ConfirmationDialog } from '@/components/dialogBox/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';

interface Props {
  openCreateOnMount?: boolean;
}

const DMOStaffManagement = ({ openCreateOnMount }: Props) => {
  const navigate = useNavigate();
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const canRead = hasPermission('dmo_staff_management_read');
  const canCreate = hasPermission('dmo_staff_management_create');
  const canUpdate = hasPermission('dmo_staff_management_update');
  const canDelete = hasPermission('dmo_staff_management_delete');

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const queryParams: DMOStaffListQueryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue]
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetDMOStaffsQuery(queryParams, { skip: !canRead });

  const [createStaff, { isLoading: isCreating }] = useCreateDMOStaffMutation();
  const [updateStaff, { isLoading: isUpdating }] = useUpdateDMOStaffMutation();
  const [deleteStaff, { isLoading: isDeleting }] = useDeleteDMOStaffMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedStaff, setSelectedStaff] = useState<DMOStaff | null>(null);

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    staff: DMOStaff | null;
  }>({ open: false, staff: null });

  useEffect(() => {
    if (!openCreateOnMount) return;
    if (!canCreate) return;

    setFormMode('create');
    setSelectedStaff(null);
    setIsFormOpen(true);
  }, [canCreate, openCreateOnMount]);

  useEffect(() => {
    if (!data) return;
    const paginated = data as PaginatedApiResponse<DMOStaff[]>;
    handleTotalPages(paginated.totalPages || 1);
    handleTotalItems(paginated.totalItems || paginated.data?.length || 0);
  }, [data, handleTotalPages, handleTotalItems]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      refetch();
    }
  };

  const handleOpenCreate = () => {
    if (!canCreate) return;
    setFormMode('create');
    setSelectedStaff(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = useCallback(
    (staff: DMOStaff) => {
      if (!canUpdate) return;
      setFormMode('edit');
      setSelectedStaff(staff);
      setIsFormOpen(true);
    },
    [canUpdate]
  );

  const handleOpenDelete = useCallback(
    (staff: DMOStaff) => {
      if (!canDelete) return;
      setConfirmState({ open: true, staff });
    },
    [canDelete]
  );

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedStaff(null);
  };

  const handleSubmit = useCallback(
    async (payload: CreateDMOStaffPayload) => {
      if (formMode === 'create') {
        const res = await createStaff(payload).unwrap();
        enqueueSnackbar('DMO staff added successfully', { variant: 'success' });
        if (res?.data?.email) {
          enqueueSnackbar(`Invitation sent to ${res.data.email}`, {
            variant: 'info',
          });
        }
      } else if (selectedStaff) {
        await updateStaff({ id: selectedStaff.id, data: payload }).unwrap();
        enqueueSnackbar('DMO staff updated successfully', {
          variant: 'success',
        });
      }
      refetch();
    },
    [
      createStaff,
      enqueueSnackbar,
      formMode,
      refetch,
      selectedStaff,
      updateStaff,
    ]
  );

  const handleCloseDelete = () => {
    setConfirmState({ open: false, staff: null });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState.staff) return;

    try {
      await deleteStaff(confirmState.staff.id).unwrap();
      enqueueSnackbar('DMO staff deleted successfully', { variant: 'success' });
      handleCloseDelete();
      refetch();
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message ||
        (err as ErrorApiResponse)?.message ||
        'Failed to delete DMO staff';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const columns: ColumnDef<DMOStaff>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-900">{row.original.name}</span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">{row.original.email}</span>
        ),
        meta: { className: 'min-w-[240px]' },
      },
      {
        id: 'phone',
        header: 'Phone',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">
            {row.original.country_code || ''} {row.original.phone_number || '-'}
          </span>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {canUpdate && (
              <button
                type="button"
                onClick={() => handleOpenEdit(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit"
                aria-label={`Edit staff ${row.original.email}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => handleOpenDelete(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete"
                aria-label={`Delete staff ${row.original.email}`}
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-gray-500"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [canDelete, canUpdate, handleOpenDelete, handleOpenEdit]
  );

  if (!canRead) {
    return (
      <Fragment>
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  Manage DMO staff users here.
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500" role="alert">
                You don't have permission to view DMO staff management.
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <h1
                id="dmo-staff-management-heading"
                className="text-xl font-semibold"
              >
                DMO Staff Management
              </h1>
              <ToolbarDescription>
                Manage DMO staff accounts. Staff will receive credentials via
                email.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => navigate('/dmo-management')}
                aria-label="Back to DMO management"
              >
                <KeenIcon
                  type="filled"
                  icon="arrow-left"
                  className="text-gray-500"
                />
                Back
              </button>
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreate}
                  aria-label="Add DMO Staff"
                >
                  Add DMO Staff
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="card card-grid w-full contrast:!bg-black contrast:!text-white">
          <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
            <div className="flex items-center gap-2">
              <label
                className="input input-sm h-9 flex items-center"
                htmlFor="search-dmo-staffs"
                aria-label="Search DMO staffs"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500 contrast:!text-white"
                />
                <input
                  id="search-dmo-staffs"
                  placeholder="Search staff"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-56 h-9 text-sm contrast:!bg-black contrast:!text-white"
                  aria-label="Search DMO staff"
                />
              </label>
              {searchTerm && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="btn btn-sm btn-light"
                  aria-label="Reset staff search"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <div className="card-body">
            <DataGrid
              columns={columns}
              data={data?.data || []}
              loading={isLoading || isFetching}
              serverSide={true}
              pagination={{
                page: pagination.pageIndex + 1,
                size: pagination.pageSize,
                sizes: [10, 20, 50],
                totalPages,
                totalItems,
                setPagination,
                info: `Page ${pagination.pageIndex + 1} of ${totalPages}`,
                more: pagination.pageIndex + 1 < totalPages,
              }}
              messages={{
                empty: isError
                  ? 'Failed to load staff list.'
                  : 'No DMO staff found.',
              }}
            />
          </div>
        </div>
      </Container>

      <DMOStaffFormModal
        open={isFormOpen}
        mode={formMode}
        initialData={selectedStaff}
        isSubmitting={isCreating || isUpdating}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmationDialog
        open={confirmState.open}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        message={
          <span>
            Are you sure you want to delete <b>{confirmState.staff?.email}</b>?
          </span>
        }
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
      />
    </Fragment>
  );
};

export default DMOStaffManagement;
